/**
 * Building the download, here, one photograph at a time.
 *
 * The server used to do this and cannot any more: a worker holds no group key,
 * so it would assemble a perfectly valid zip full of files nobody can open.
 * Everything below runs where the keys are.
 *
 * Two things this is careful about, both of which are about phones:
 *
 * Photographs are fetched **one at a time**, not with `Promise.all`. Forty
 * concurrent decryptions is forty decoded images resident at once, which is how
 * a tab is killed on a phone; it also saturates the connection so the first
 * photograph finishes at the same time as the last, and the progress bar sits
 * at zero for a minute.
 *
 * And where the browser allows it the archive is **streamed to disk** rather
 * than held in memory. A 300-photo selection is over a gigabyte, and a Blob of
 * that size is a Blob the browser has to find room for before anything is
 * saved.
 */

import { openFileKey } from "../../lib/keyring.js";
import { decrypt } from "@fc/core";
import { fetchSignedUrls } from "../../lib/signedUrls.js";
import { ZipWriter, memorySink, type ZipSink } from "./zip.js";

/**
 * Above this, ask where to put it and stream it there.
 *
 * Streaming to disk is the right answer for a large archive and the wrong
 * one for a small: `showSaveFilePicker` opens a native dialog, so choosing
 * three photographs would put a file-save prompt in front of somebody who
 * pressed Download and expected a download. Below the threshold the archive
 * is assembled in memory and saved the way every other download on the web
 * works.
 *
 * 256 MB because that is roughly where holding the whole thing as a Blob
 * starts to matter on a phone — and a phone is also where the picker does
 * not exist, so the fallback has to stay sane there.
 */
const STREAM_ABOVE_BYTES = 256 * 1024 * 1024;

export interface ZipProgress {
  done: number;
  total: number;
  /** Names of photographs that could not be included, with the reason. */
  skipped: { name: string; why: string }[];
}

interface FileSystemPicker {
  showSaveFilePicker?: (options: {
    suggestedName?: string;
    types?: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<{
    createWritable: () => Promise<{
      write: (data: Uint8Array) => Promise<void>;
      close: () => Promise<void>;
    }>;
  }>;
}

/**
 * Somewhere to put the archive, preferring disk.
 *
 * Returns null when the picker exists but the person cancelled it, which is not
 * an error and must not be reported as one.
 */
async function chooseSink(
  suggestedName: string,
  estimatedBytes: number,
): Promise<{ sink: ZipSink; save: () => void } | null> {
  const picker = (window as unknown as FileSystemPicker).showSaveFilePicker;

  if (picker && estimatedBytes > STREAM_ABOVE_BYTES) {
    let handle;
    try {
      handle = await picker({
        suggestedName,
        types: [
          {
            description: "Zip archive",
            accept: { "application/zip": [".zip"] },
          },
        ],
      });
    } catch {
      /* Cancelled the dialog, or the call was not from a user gesture. */
      return null;
    }

    const writable = await handle.createWritable();
    return {
      sink: {
        write: (chunk) => writable.write(chunk),
        close: () => writable.close(),
      },
      /* Already on disk by the time this is called; nothing left to do. */
      save: () => undefined,
    };
  }

  const sink = memorySink();
  return {
    sink,
    save: () => {
      const url = URL.createObjectURL(sink.blob());
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = suggestedName;
      anchor.click();
      /* After the browser has taken its copy. */
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  };
}

/**
 * The name the archive should use, given what was actually delivered.
 *
 * A `preview` is a WebP or an AVIF regardless of what the original was, so an
 * archive built from previews is full of WebPs called `.jpg`. Some viewers
 * sniff the content and open them anyway; others refuse, and the person is
 * left with a folder of photographs their computer says are corrupt. The name
 * follows the bytes.
 *
 * Originals keep their own name untouched: the extension there is already the
 * truth, and rewriting it would be the same mistake in reverse.
 */
function nameFor(name: string, format: string | undefined, isOriginal: boolean): string {
  if (isOriginal || !format) return name;

  const extension = format.toLowerCase().replace(/^image\//, "");
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;

  /* Already right — a JPEG preview of a JPEG needs no renaming. */
  if (dot > 0 && name.slice(dot + 1).toLowerCase() === extension) return name;

  return `${stem}.${extension}`;
}

/**
 * Fetch, decrypt and zip a selection.
 *
 * Resolves with what was skipped. A selection where three photographs are
 * missing derivatives should still produce an archive of the other ninety-seven
 * and say which three — refusing the lot because of one is the behaviour that
 * makes people download in ones and twos for ever.
 */
export async function downloadAsZip(
  groupId: string,
  /* Names come from the caller: the grid already has them, and a second
     round trip to re-fetch what is on screen is a round trip for nothing. */
  files: { id: string; name: string; sizeBytes?: number }[],
  variant: "preview" | "original",
  onProgress: (progress: ZipProgress) => void,
  signal?: AbortSignal,
): Promise<ZipProgress> {
  const stamp = new Date().toISOString().slice(0, 10);
  const estimated = files.reduce((sum, file) => sum + (file.sizeBytes ?? 0), 0);
  const chosen = await chooseSink(`photos-${stamp}.zip`, estimated);
  if (!chosen) {
    return { done: 0, total: files.length, skipped: [] };
  }

  const zip = new ZipWriter(chosen.sink);
  const progress: ZipProgress = {
    done: 0,
    total: files.length,
    skipped: [],
  };

  /*
   * Signed one at a time, deliberately. They expire, and a URL signed at the
   * start of a three-hundred-photograph download would be stale long before
   * its turn came.
   */
  for (const file of files) {
    if (signal?.aborted) break;

    const { id: fileId, name } = file;

    try {
      const [signed] = await fetchSignedUrls(groupId, [fileId], variant);
      if (!signed?.url) {
        progress.skipped.push({ name, why: "no file of that size" });
        continue;
      }

      const response = await fetch(signed.url, {
        credentials: "omit",
        signal,
      });
      if (!response.ok) {
        progress.skipped.push({
          name,
          why: `storage said ${String(response.status)}`,
        });
        continue;
      }

      const raw = new Uint8Array(await response.arrayBuffer());

      /* A file stored before encryption existed goes in as it comes out. */
      const plain = signed.wrappedFileKey
        ? await decrypt(raw, await openFileKey(groupId, signed.wrappedFileKey))
        : raw;

      await zip.addFile(
        nameFor(name, signed.format, variant === "original"),
        plain,
      );
    } catch (error) {
      /*
       * A size limit ends the archive; anything else skips one photograph.
       * Continuing past a full zip would write entries the directory cannot
       * describe, which is the truncated-but-openable outcome to avoid.
       */
      if (error instanceof Error && error.name === "ZipTooLargeError") throw error;
      if (signal?.aborted) break;
      progress.skipped.push({
        name,
        why: error instanceof Error ? error.message : "could not be read",
      });
    } finally {
      progress.done += 1;
      onProgress({ ...progress, skipped: [...progress.skipped] });
    }
  }

  await zip.close();
  chosen.save();
  return progress;
}
